import { Box, MenuItem, Typography } from '@mui/material'
import { Header } from '../../components/header/Header'
import styles from './MedicineModal.module.css'
import { useTranslation } from 'react-i18next'
import style from '../calendar/Editor.module.css'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import {
  emptyTreatmentDTO,
  fromModel,
  TreatmentDTO,
} from '../../modules/treatments/models/TreatmentDTO'
import { UnitType, unitTypeTypes } from '../../modules/treatments/enums/UnitType'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { getContentContainer } from '../../container/treatment-module'
import { ITreatmentService, TREATMENT_SERVICE_KEY } from '../../modules/treatments'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import { Treatment } from '../../modules/treatments/models/Treatment'
import close from '../../assets/table_icons/delete.svg'

const userContainer = getUserContainer()
const treatmentService = getContentContainer().get<ITreatmentService>(TREATMENT_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

// Modos del modal
export enum MedicineModalMode {
  VIEW = 'view',
  CREATE = 'create',
  EDIT = 'edit',
}

interface MedicineModalProps {
  mode: MedicineModalMode
  handleClose: () => void
  currentTreatment?: Treatment
  handleLoading?: () => void
}

export const MedicineModal: React.FC<MedicineModalProps> = ({
  mode,
  handleClose,
  currentTreatment,
  handleLoading,
}) => {
  const loggedUser = loggedUserService.get()
  const [treatment, setTreatment] = useState<TreatmentDTO>(emptyTreatmentDTO())
  const { t } = useTranslation()

  const isViewMode = mode === MedicineModalMode.VIEW
  const isEditMode = mode === MedicineModalMode.EDIT
  const isCreateMode = mode === MedicineModalMode.CREATE

  useEffect(() => {
    if (currentTreatment) {
      setTreatment(fromModel(currentTreatment))
    }
  }, [currentTreatment])

  const handleTreatment = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    setTreatment({
      ...treatment,
      [name]: name === 'dose' ? (value === '' ? 0 : parseInt(value, 10) || 0) : value,
    })
  }

  const handleSelectChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTreatment({
      ...treatment,
      [e.target.name]: parseInt(e.target.value, 10),
    })
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validar que el formulario es válido
    const form = e.currentTarget as HTMLFormElement
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    // Validar que el nombre no esté vacío
    if (!treatment.name || treatment.name.trim() === '') {
      return
    }

    // Validar que la dosis sea mayor que 0
    if (treatment.dose <= 0) {
      return
    }

    const newTreatment = {
      ...treatment,
      creatorID: loggedUser?.id ?? '',
    }

    if (isEditMode && currentTreatment) {
      treatmentService.update(newTreatment).subscribe((res) => {
        handleLoading?.()
        handleClose()
      })
      return
    }

    if (isCreateMode) {
      treatmentService.add(newTreatment).subscribe((res) => {
        handleLoading?.()
        handleClose()
      })
    }
  }

  const handleSubmitButton = () => {
    const form = document.querySelector('form') as HTMLFormElement
    if (form) {
      form.requestSubmit()
    }
  }

  const getUnitLabel = (unit: UnitType): string => {
    const unitTypes = unitTypeTypes()
    return unitTypes[unit] || ''
  }

  const getModalTitle = () => {
    switch (mode) {
      case MedicineModalMode.VIEW:
        return t('seeMedicine')
      case MedicineModalMode.CREATE:
        return t('addMedicine')
      case MedicineModalMode.EDIT:
        return t('editMedicine')
      default:
        return t('addMedicine')
    }
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.content}>
        <Header label={getModalTitle()} icon={close} onClick={handleClose} />

        <form onSubmit={isViewMode ? undefined : handleSave}>
          {/* Nombre */}
          <Box mb={3} display="flex" alignItems="center">
            <Box width="30%" mr={2}>
              <p className={style.label} style={{ margin: 0 }}>
                {t('name')}
              </p>
            </Box>
            <Box width="70%">
              {isViewMode ? (
                <Typography>{treatment.name}</Typography>
              ) : (
                <Input
                  fullWidth
                  value={treatment.name}
                  id="name"
                  name="name"
                  label=""
                  variant="outlined"
                  required
                  onChange={handleTreatment}
                />
              )}
            </Box>
          </Box>

          {/* Dosis por toma */}
          <Box mb={3} display="flex" alignItems="center">
            <Box width="30%" mr={2}>
              <p className={style.label} style={{ margin: 0 }}>
                {t('dosePerIntake')}
              </p>
            </Box>
            <Box width="70%" display="flex" alignItems="center" gap={2}>
              {/* Dosis */}
              <Box flex={1}>
                {isViewMode ? (
                  <Typography>{treatment.dose}</Typography>
                ) : (
                  <Input
                    type="text"
                    variant="outlined"
                    fullWidth
                    id="dose"
                    name="dose"
                    value={treatment.dose === 0 ? '' : treatment.dose.toString()}
                    label=""
                    required
                    onChange={handleTreatment}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: '1',
                    }}
                  />
                )}
              </Box>

              {/* Unidad */}
              <Box flex={1}>
                {isViewMode ? (
                  <Typography>{getUnitLabel(treatment.unit)}</Typography>
                ) : (
                  <Input
                    select
                    variant="outlined"
                    fullWidth
                    id="unit"
                    name="unit"
                    value={treatment.unit}
                    label=""
                    onChange={handleSelectChange}
                  >
                    {Object.values(UnitType)
                      .filter((value) => typeof value === 'number')
                      .map((unitValue) => (
                        <MenuItem key={unitValue} value={unitValue}>
                          {getUnitLabel(unitValue as UnitType)}
                        </MenuItem>
                      ))}
                  </Input>
                )}
              </Box>
            </Box>
          </Box>

          {/* Botones - Solo mostrar en modo crear/editar */}
          {!isViewMode && (
            <Box className={styles.buttons} display="flex" justifyContent="center" mt={4}>
              <Box sx={{ mr: 1 }}>
                <AppButton
                  theme={ButtonTheme.NewPrimary}
                  type="button"
                  label={isEditMode ? t('save') : t('add')}
                  handler={handleSubmitButton}
                />
              </Box>
              <AppButton
                theme={ButtonTheme.NewSecondary}
                type="button"
                label={t('cancel')}
                handler={handleClose}
              />
            </Box>
          )}
        </form>
      </Box>
    </Box>
  )
}
